import DocumentSchema from '@/components/DocumentSchema';
import type { Table } from 'schema-dts';
import type { Peer } from '../../MultipleVsPeers';
import Tooltip from '@/components/Tooltip';
import {
  TooltipName,
  TipContainer,
  PeerDataTable,
  PeerAverageRow,
  PeerAverageTitle,
  CompanyNameCell,
  TableCell,
  TableSymbol,
  TableSpan,
} from './styled';
import useGlossaryTerms from '@/hooks/useGlossaryTerms';
import type { PreferredMultiple } from '../../../../selector';
import { useTracking } from 'react-tracking';
import { push } from 'redux-first-router';
import { RouterLink } from '@redux-first-router-link';
import { toLocaleUrl } from '@/pages/CompanyReport/utils/toLocaleUrl';
import { useLocale } from '@/hooks/useLocale';
import { CompanyReportTableRow } from '@/pages/CompanyReport/styled';
import { useTranslation } from '@/hooks/useTranslation';

const useForwardMetricLabel = (isForward: boolean) => {
  const t = useTranslation();
  return (metric: 'PE' | 'PS' | 'PB') => {
    if (isForward) {
      return metric;
    }

    switch (metric) {
      case 'PE':
        return t('page.companyReport.valuation.vsPeers.forwardPE');
      case 'PB':
        return t('page.companyReport.valuation.vsPeers.forwardPB');
      case 'PS':
        return t('page.companyReport.valuation.vsPeers.forwardPS');
    }
  };
};

interface Props {
  companyId: string;
  tickerSymbol: string;
  peers: Peer[];
  averageFormatted: string;
  preferredMultiple: PreferredMultiple;
  useForwardRatio?: boolean;
}

const MultipleVsPeersTable = ({
  companyId,
  tickerSymbol,
  peers,
  averageFormatted,
  preferredMultiple,
  useForwardRatio,
}: Props) => {
  const getTerm = useGlossaryTerms();
  const { trackEvent } = useTracking();
  const locale = useLocale();

  const t = useTranslation();
  const getForwardMetricLabel = useForwardMetricLabel(useForwardRatio ?? false);

  return (
    <>
      <DocumentSchema<Table>
        item={{
          '@context': 'https://schema.org',
          '@type': 'Table',
          headline: t('page.companyReport.valuation.vsPeers.table.heading', {
            tickerSymbol,
            metric: preferredMultiple,
          }),
          about: t('page.companyReport.valuation.vsPeers.table.description', {
            tickerSymbol,
            metric: preferredMultiple,
          }),
        }}
      />
      <PeerDataTable data-cy-id="valuation-multiple-vs-peers-table">
        <caption>
          {t('page.companyReport.valuation.vsPeers.table.description', {
            tickerSymbol,
            metric: preferredMultiple,
          })}
        </caption>
        <thead>
          <tr>
            <th>
              {t('page.companyReport.valuation.vsPeers.table.header.company')}
            </th>
            <th>
              <Tooltip
                openDelay={300}
                light
                title={
                  <TipContainer>
                    {getTerm(
                      `${
                        useForwardRatio ? 'forward ' : ''
                      }${preferredMultiple.toLowerCase()} ratio`
                    )}
                  </TipContainer>
                }
              >
                <TooltipName>
                  {getForwardMetricLabel(preferredMultiple)}
                </TooltipName>
              </Tooltip>
            </th>
            <th>
              <Tooltip
                openDelay={300}
                light
                title={
                  <TipContainer>
                    {getTerm(
                      `estimated ${
                        preferredMultiple === 'PS' ? 'sales' : 'earnings'
                      } growth`
                    )}
                  </TipContainer>
                }
              >
                <TooltipName>
                  {t(
                    'page.companyReport.valuation.vsPeers.table.header.growth'
                  )}
                </TooltipName>
              </Tooltip>
            </th>
            <th>
              <Tooltip
                openDelay={300}
                light
                title={
                  <TipContainer>
                    {getTerm('market capitalization')}
                  </TipContainer>
                }
              >
                <TooltipName>
                  {t(
                    'page.companyReport.valuation.vsPeers.table.header.marketCap'
                  )}
                </TooltipName>
              </Tooltip>
            </th>
          </tr>
        </thead>
        <tbody>
          <PeerAverageRow>
            <PeerAverageTitle>
              {t('page.companyReport.valuation.vsPeers.table.row.peerAverage')}
            </PeerAverageTitle>
            <TableCell selected>{averageFormatted}</TableCell>
            <td>{''}</td>
            <td>{''}</td>
          </PeerAverageRow>
          {peers.map((peer) => {
            const selected =
              peer.companyId.toLowerCase() === companyId.toLowerCase();
            const valuationPath = `${toLocaleUrl(
              locale,
              peer.canonicalURL
            )}/valuation`;
            return (
              <CompanyReportTableRow
                selected={selected}
                key={peer.companyId}
                onClick={() => {
                  if (selected) return;
                  if (RUNTIME_ENV === 'native') {
                    push(valuationPath);
                  } else {
                    window.open(valuationPath, '_blank');
                  }
                  trackEvent({
                    action: 'click',
                    modifier: 'vsPeersChart',
                    companyId: peer.companyId,
                  });
                }}
              >
                <td>
                  <CompanyNameCell selected={selected}>
                    <TableSymbol
                      as={RUNTIME_ENV === 'native' ? RouterLink : 'a'}
                      href={selected ? undefined : valuationPath}
                      to={valuationPath}
                      target={RUNTIME_ENV === 'native' ? undefined : '_blank'}
                    >
                      <b data-cy-id="ticker-symbol">
                        {peer.tickerSymbol + ' '}
                      </b>
                    </TableSymbol>
                    <TableSpan selected={selected}>{peer.name}</TableSpan>
                  </CompanyNameCell>
                </td>
                <TableCell selected={selected}>{peer.ratioFormatted}</TableCell>
                <TableCell selected={selected}>
                  {preferredMultiple === 'PS'
                    ? peer.salesGrowthFormatted
                    : peer.earningsGrowthFormatted}
                </TableCell>
                <TableCell selected={selected}>
                  {peer.marketCapFormatted}
                </TableCell>
              </CompanyReportTableRow>
            );
          })}
        </tbody>
      </PeerDataTable>
    </>
  );
};
export default MultipleVsPeersTable;
